<template>
  <vs-autocomplete
    ref="autocomplete"
    multiselect
    :disabled="disabled"
    label="Tags"
    empty-state-text="No existing tags yet. Start typing to create one."
    :value="value"
    :items="options"
    :search-value.sync="searchValue"
    @input="handleInput"
    @keydown.enter="handleHotKeys"
    @keydown.tab="handleHotKeys"
  >
    <template v-if="showCustomTagOption" #append-item>
      <v-divider class="mb-2" />
      <v-list-item data-test="create-tile" @click.stop="handleCustomAdd">
        <v-list-item-content>
          <vs-text>Create "{{ searchValue }}" tag</vs-text>
        </v-list-item-content>
      </v-list-item>
    </template>
  </vs-autocomplete>
</template>

<script setup lang="ts">
import { useGetTagsQuery } from '@/api/tags'
import { ref, computed } from 'vue'
import { debounce } from 'lodash'
import { useQueryClient } from '@tanstack/vue-query'

interface Props {
  value: string[]
  disabled?: boolean
  ownerId: string
}
const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'input', value: string[]): void
}>()

const searchValue = ref<string>('')
const autocomplete = ref<any>(null)

const { data: tagOptions } = useGetTagsQuery()
const queryClient = useQueryClient()

const options = computed(() => {
  if (!tagOptions.value) return []

  const values = new Set([
    ...tagOptions.value.map((tag: { name: string }) => tag.name),
    ...props.value,
  ])
  return Array.from(values).map((tag) => ({ text: tag, value: tag }))
})

const matchingOption = computed(() => {
  const match = options.value.find(
    (option) => option.text.toLowerCase() === searchValue.value.toLowerCase(),
  )
  return match ? match.text : undefined
})

const isSearchAlreadyAdded = computed(() => {
  const lowerCaseValues = props.value.map((tag) => tag.toLowerCase())
  return lowerCaseValues.includes(searchValue.value.toLowerCase())
})

const showCustomTagOption = computed(() => {
  return searchValue.value.length > 0 && !matchingOption.value
})

const handleInput = debounce((newValue: string[]) => {
  emit('input', newValue)
}, 500)

function resetField() {
  searchValue.value = ''
  if (autocomplete.value) {
    autocomplete.value.focus()
    if (typeof autocomplete.value.clearSearchValue === 'function') {
      autocomplete.value.clearSearchValue()
    }
  }
}

function handleAddMatchingOption() {
  if (matchingOption.value) {
    handleInput([...props.value, matchingOption.value])
    resetField()
  }
}

function handleCustomAdd() {
  handleInput([...props.value, searchValue.value])

  const newTag = { name: searchValue.value, ownerId: props.ownerId }
  queryClient.setQueryData(['tags'], (oldData) => {
    return Array.isArray(oldData) ? [...oldData, newTag] : [newTag]
  })

  resetField()
}

function handleHotKeys(event: KeyboardEvent) {
  if (searchValue.value.length) {
    event.preventDefault()
    if (isSearchAlreadyAdded.value) {
      resetField()
    } else if (matchingOption.value) {
      handleAddMatchingOption()
    } else {
      handleCustomAdd()
    }
  }
}
</script>
